import React, { useEffect, useState, type ReactNode } from 'react';

import { useEvent } from '@mint-lib/hooks';
import type { IconsList as Icons } from '@mint-lib/icons/allIcons';
import { Slot } from '@mint-lib/slots';
import { type Theme } from '@mint-lib/styled';
import { typographyVariants } from '@mint-lib/tw-components';
import '@mint-lib/theme/src/extensions/palette.js';
import type { ParsedLocation } from '@mint/routing';
import AppBarM, { AppBarProps as AppPropsM } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import Breadcrumbs from '../Breadcrumbs/Breadcrumbs.jsx';
import Drawer from '../Drawer/Drawer.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Typography from '../Typography/Typography.jsx';

export interface TabsInterface {
  text: string;
  icon: Icons;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

export interface UserProfile {
  email: string;
  profile: {
    firstName: string;
    lastName: string;
    avatar: string;
    company: {
      name: string;
    };
  };
}

export interface TopBarProps extends AppPropsM {
  /**
   * Page's title
   */
  title?: string;
  /**
   * Popover content
   */
  profilePopoverContent?: React.ReactNode;
  workspacesPopoverContent?: React.ReactNode;

  /**
   * callback for onClick on Logout Button in PopoverContent
   */
  onLogoutClick?: () => void;
  /**
   * callback for onClick on Settings buttonIcon in PopoverContent
   */
  onSettingsButtonClick?: () => void;
  profileData?: UserProfile;
  clientName?: string;
  adornments?: React.ReactNode;
  /**
   * Content centered between the breadcrumbs and the adornments (desktop only)
   */
  centerContent?: React.ReactNode;
  isNewChat?: boolean;
  isMobileEnabled?: boolean;
  aiAgentsButtonContent?: React.ReactNode;
  currentChatTitle?: React.ReactNode;
  location?: ParsedLocation;
}

const TopBar = ({
  children,
  position = 'sticky',
  onLogoutClick,
  onSettingsButtonClick,
  profilePopoverContent,
  workspacesPopoverContent,
  title,
  profileData,
  clientName,
  adornments,
  centerContent,
  isNewChat,
  isMobileEnabled,
  aiAgentsButtonContent,
  currentChatTitle,
  location,
  ...props
}: TopBarProps) => {
  const [hasCampaignTitle, setHasCampaignTitle] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const mobileAppbarColor = (theme: Theme) => {
    return isNewChat ? 'transparent' : theme.palette.ui.secondary;
  };

  useEffect(() => {
    if (isMobileEnabled) {
      setIsSidebarOpen(false);
    }
  }, [location]);

  const onTitleCampaignChange = useEvent((receivedData: ReactNode | null) => {
    setHasCampaignTitle(receivedData !== null && receivedData !== undefined);
  });

  const renderDrawerBackdrop = () => {
    if (!isSidebarOpen) {
      return null;
    }

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'ui.overlay',
          zIndex: (theme) => theme.zIndex.drawer - 1, // Ensure it's behind the drawer but above other content
        }}
        onClick={() => setIsSidebarOpen(false)}
      />
    );
  };

  return (
    <AppBarM
      position={isMobileEnabled ? 'fixed' : position}
      color={isMobileEnabled ? 'secondary' : 'inherit'}
      elevation={0}
      {...props}
      sx={{
        display: 'flex',
        height: isMobileEnabled ? '4rem' : '3rem',
        left: isMobileEnabled ? 0 : '48px',
        width: isMobileEnabled ? '100dvw' : 'calc(100% - 48px)',
        flexDirection: 'row',
        alignItems: isMobileEnabled ? 'center' : 'unset',
        justifyContent: 'space-between',
        backgroundColor: (theme) =>
          `${
            isMobileEnabled
              ? mobileAppbarColor(theme)
              : theme.palette.ui.primary
          } !important`,
        borderBottom: (theme) =>
          `1px solid ${isMobileEnabled ? 'transparent' : theme.palette.ui.tertiary}`,
      }}
    >
      <>
        {isMobileEnabled ? (
          <>
            <div className="flex h-4 w-[calc(100%-110px)] flex-row gap-4">
              <IconButton
                icon="BurgerMenu"
                onClick={() => setIsSidebarOpen(true)}
                variant="plain"
                aria-label="Open sidebar"
                sx={{
                  '&.plain:focus': {
                    boxShadow: 'none',
                  },
                  marginLeft: 2,
                  '& .MuiSvgIcon-root': {
                    width: 24,
                    height: 24,
                    '& path': {
                      stroke: (theme) => theme.palette.icon.primary,
                    },
                  },
                }}
              />
              {currentChatTitle}
            </div>
            {aiAgentsButtonContent}
            {renderDrawerBackdrop()}
            <Drawer
              variant="persistent"
              anchor="left"
              open={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
              PaperProps={{
                sx: {
                  maxWidth: '328px',
                  width: '100%',
                  padding: '0 24px 24px 24px',
                  flexGrow: 0,
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                  background: (theme) => theme.palette.ui.primary,
                  color: (theme) => theme.palette.text.secondary,
                  border: 'none !important',
                  borderRight: 'none !important',
                },
              }}
              SlideProps={{
                timeout: 700,
                easing: {
                  enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }}
              sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div className="sticky top-0 z-popover -ml-6 flex w-[328px] flex-col justify-between gap-2 bg-ui-primary px-6 pt-10 shadow-[0_1px_0_0_var(--ui-primary)]">
                {/* user-workspaces mf */}
                <div className="flex w-full flex-row items-center justify-between py-4">
                  <div className="flex flex-row">
                    {profilePopoverContent}
                    <div className="flex flex-col justify-center gap-1">
                      <p
                        className={typographyVariants({
                          variant: 'bodyShort02',
                          color: 'primary',
                        })}
                      >
                        {`${profileData?.profile?.firstName} ${profileData?.profile?.lastName}`}
                      </p>
                      <p
                        className={typographyVariants({
                          variant: 'caption2',
                          color: 'primary',
                        })}
                      >
                        {`${profileData?.email}`}
                      </p>
                    </div>
                  </div>

                  <IconButton
                    onClick={() => setIsSidebarOpen(false)}
                    icon="Close"
                    variant="icon"
                    aria-label="Close sidebar"
                    sx={{
                      '& .MuiSvgIcon-root': {
                        width: 24,
                        height: 24,
                        color: (theme) => theme.palette.icon.primary,
                      },
                      '&.icon': {
                        '&:focus': {
                          boxShadow: 'none !important',
                        },
                        '&:hover': {
                          backgroundColor: 'transparent !important',
                        },
                      },
                    }}
                  />
                </div>

                <div className="flex w-full flex-row items-center justify-between">
                  {workspacesPopoverContent}
                </div>
              </div>
              <Slot name="sidebar" />
            </Drawer>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              marginLeft: '1rem',
            }}
          >
            <Breadcrumbs>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                {/* user-workspaces mf */}
                {workspacesPopoverContent}

                {hasCampaignTitle ? (
                  <Divider flexItem orientation="vertical" />
                ) : null}
                <Typography variant="bodyShort01">
                  <Slot
                    name="topBarCampaignTitle"
                    onRender={onTitleCampaignChange}
                  />
                </Typography>
              </Box>
            </Breadcrumbs>
          </Box>
        )}
        {!isMobileEnabled && centerContent && (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minWidth: 0,
              alignItems: 'center',
              justifyContent: 'center',
              paddingX: '1rem',
            }}
          >
            {centerContent}
          </Box>
        )}
        {!isMobileEnabled && (
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {adornments}
            {profilePopoverContent}
          </Box>
        )}
      </>
    </AppBarM>
  );
};

export default TopBar;
